/* ============================================================================
   Contractor-side UI language. English is the source of truth; t() looks the
   exact English string up in the zh dictionary and falls through untouched
   when there is no entry, so partial coverage degrades gracefully.
   ========================================================================== */
import { useEffect, useState } from "react";
/* The larger Chinese dictionaries (ZH2–ZH6, ~130 KB) are fetched only for
   someone using Chinese, instead of riding in every English user's first
   download. The app waits for them before its first paint when the language
   is Chinese (i18nReady), so nobody sees English flash first. */
let extra: Record<string, string> | null = null;
let loadingZh: Promise<void> | null = null;
function loadZh(): Promise<void> {
  if (extra) return Promise.resolve();
  if (!loadingZh) {
    loadingZh = Promise.all([import("./i18nZh2"), import("./i18nZh3"), import("./i18nZh4"), import("./i18nZh5"), import("./i18nZh6")])
      .then(([a, b, c, d, e]) => { extra = { ...a.ZH2, ...b.ZH3, ...c.ZH4, ...d.ZH5, ...e.ZH6 }; DICT = { ...ZH, ...extra }; subs.forEach((f) => f()); })
      .catch(() => { loadingZh = null; });   // offline: English for now, retried on the next switch
  }
  return loadingZh;
}

export type Lang = "en" | "zh";
const KEY = "atelier.lang";

let lang: Lang = "en";
try {
  const stored = localStorage.getItem(KEY);
  if (stored === "zh") lang = "zh";
  // a first visit from a Chinese-language browser starts in Chinese — the
  // app's own translation, not the browser's rewrite of the page
  else if (!stored && typeof navigator !== "undefined" && /^zh/i.test(navigator.language || "")) lang = "zh";
} catch { /* SSR/quota */ }
try { document.documentElement.lang = lang === "zh" ? "zh-CN" : "en"; } catch { /* SSR */ }

const subs = new Set<() => void>();

export function getLang(): Lang { return lang; }
/** Resolves once the dictionaries for the current language are in. */
export function i18nReady(): Promise<void> {
  if (lang !== "zh") return Promise.resolve();
  // never hold the first paint more than 2.5 s for a slow site connection
  return Promise.race([loadZh(), new Promise<void>((r) => setTimeout(r, 2500))]);
}
export function setLang(l: Lang) {
  if (l === "zh") void loadZh();
  lang = l;
  try { localStorage.setItem(KEY, l); } catch { /* quota */ }
  try { document.documentElement.lang = l === "zh" ? "zh-CN" : "en"; } catch { /* SSR */ }
  subs.forEach((f) => f());
}
/** Subscribe a component to language changes (components using t() call this). */
export function useLang(): Lang {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return lang;
}

export function t(s: string): string {
  return lang === "zh" ? DICT[s] ?? s : s;
}

/** EN / 中文 pill toggle for the contractor shell. */
export function LangToggle() {
  const l = useLang();
  return (
    <div className="row" style={{ gap: 6, justifyContent: "center" }}>
      {(["en", "zh"] as Lang[]).map((k) => (
        <button key={k} className={"chip" + (l === k ? " on" : "")} style={{ fontSize: 11, padding: "4px 12px" }} onClick={() => setLang(k)}>
          {k === "en" ? "EN" : "中文"}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- dictionary -- */
const ZH: Record<string, string> = {
  /* my tasks */
  "My Tasks": "我的任务",
  "everything issued to the team": "发给团队的全部任务",
  "Newly issued": "新下达",
  "newly issued": "项新任务",
  "since you last looked": "自上次查看以来",
  "across all sources": "所有来源合计",
  "needs attention today": "今日需处理",
  "most recent shown below": "最近的显示在下方",
  "Site directive": "现场指令",
  "Meeting action": "会议行动项",
  "Punch item": "整改项",
  "RFI in court": "待回复RFI",
  "Today's target": "今日目标",
  NEW: "新",
  For: "负责人",
  issued: "下达于",
  Mine: "我的",
  Everyone: "全部人员",
  Completed: "已完成",
  "Recently completed": "最近完成",
  "Mark done": "标记完成",
  Reopen: "重新打开",
  "Open the full record": "查看完整记录",
  "Answered in the RFI section": "请在RFI栏目中回复",
  "Nothing open — every issued task is done. New directives, actions and punch items land here.": "没有待办任务 — 所有已下达的任务均已完成。新的指令、行动项和整改项会显示在这里。",
  "Ticking here updates the source record — site visit, meeting minutes, punch list or daily targets. RFIs are answered in their own section.": "在此打钩会同步更新原始记录（现场巡查、会议纪要、整改清单或今日目标）。RFI请在其栏目中回复。",

  /* drawing register management */
  "Search number or title…": "搜索图号或图名…",
  "No sheets match your search.": "没有匹配的图纸。",
  Manage: "管理",
  "Manage revisions & removal": "管理版本与删除",
  "revisions & management": "版本与管理",
  Current: "当前版本",
  Superseded: "已作废",
  View: "查看",
  "Original on file": "原始文件已存档",
  "No original": "无原始文件",
  "Real preview": "真实预览",
  Placeholder: "占位图",
  "Download this revision's original": "下载此版本的原始文件",

  /* shell / nav */
  "Contractor View": "承包商视图",
  "Site Admin": "现场管理员",
  Consultant: "顾问",
  Technician: "技术员",
  Viewer: "查看者",
  Dashboard: "仪表盘",
  Drawings: "图纸",
  "Work Program": "施工进度计划",
  "Daily Targets": "每日目标",
  "Daily Reports": "每日报告",
  "Site Visits": "现场巡查",
  Observations: "现场观察",
  Gallery: "照片库",
  RFIs: "技术询问",
  Materials: "材料",
  "Punch List": "缺陷清单",
  Reports: "进度报告",
  Contractor: "承包商",
  "UI language — submissions in Chinese are translated for the studio automatically.":
    "界面语言 — 用中文提交的内容会自动翻译成英文发送给工作室。",
  Home: "首页",
  Daily: "日报",
  More: "更多",
  Notifications: "通知",
  "All caught up.": "没有新通知。",
  "Sign out": "退出登录",
  "Team & Invites": "团队与邀请",
  "Client Portal": "业主门户",
  "Field guide": "现场指南",

  /* shared */
  Cancel: "取消",
  Note: "备注",
  Location: "位置",
  Photo: "照片",
  Attach: "附加照片",
  Date: "日期",
  Status: "状态",
  Open: "未关闭",
  Closed: "已关闭",
  Answered: "已答复",
  All: "全部",
  "Written in Chinese? It will be translated to English for the studio when you submit.":
    "用中文填写即可 — 提交时会自动翻译成英文发送给工作室。",

  /* materials */
  "Material requests": "材料申请",
  procurement: "采购",
  "Request material": "申请材料",
  "Awaiting decision": "待审批",
  "requested / under review": "已申请 / 审核中",
  "On order": "已下单",
  "with suppliers": "供应商处理中",
  "Needed within 7 days": "7天内需到场",
  "not yet delivered": "尚未送达",
  Delivered: "已送达",
  "to site": "已到现场",
  Ref: "编号",
  Item: "材料名称",
  Qty: "数量",
  "Needed by": "需求日期",
  Procurement: "采购状态",
  Supplier: "供应商",
  Requested: "已申请",
  "Under review": "审核中",
  Approved: "已批准",
  Ordered: "已下单",
  Rejected: "已拒绝",
  "with the studio": "待工作室处理",
  "Send to Mind Map": "发送至采购板",
  "Sending…": "发送中…",
  "Specification reference": "规格 / 图纸引用",
  Quantity: "数量",
  Unit: "单位",
  "Needed on site by": "需到场日期",
  "Submit request": "提交申请",
  "No requests yet.": "暂无申请。",

  /* RFIs */
  "New RFI": "新建询问",
  Subject: "主题",
  Question: "问题",
  "Answer needed by": "需答复日期",
  "Raise RFI": "提交询问",
  Overdue: "已逾期",
  open: "未答复",

  /* observations */
  "Observations & log": "现场观察与日志",
  "Add observation": "添加观察",
  "Information log": "信息日志",
  Safety: "安全",
  Quality: "质量",
  Progress: "进度",
  Design: "设计",
  General: "一般",
  "What did you observe?": "您观察到了什么？",
  "Log observation": "记录观察",
  "With the studio for response.": "待工作室答复。",

  /* meetings */
  Meetings: "会议",
  "Schedule meeting": "安排会议",
  Schedule: "安排",
  "Next meeting": "下次会议",
  "nothing scheduled": "暂无安排",
  Planned: "已安排",
  planned: "已安排",
  "with agendas ready": "议程已就绪",
  "Open actions": "未完成行动项",
  "from meeting minutes": "来自会议纪要",
  Held: "已举行",
  held: "举行于",
  "minutes on record": "已存档纪要",
  points: "项议程",
  "points discussed": "项已讨论",
  actions: "项行动",
  today: "今天",
  tomorrow: "明天",
  Title: "标题",
  Kind: "类型",
  "Site walk": "现场巡查",
  Coordination: "协调",
  Commercial: "商务",
  Other: "其他",
  Time: "时间",
  Attendees: "参会人员",
  "Agenda — one point per line": "议程 — 每行一项",
  "Everyone on the team is emailed the invitation; agenda points can still be added until the meeting is held.":
    "会议邀请将发送给全体团队成员；会议举行前仍可添加议程项。",
  "No meetings yet — schedule the first and build its agenda.": "暂无会议 — 安排第一次会议并制定议程。",
  "No agenda points yet.": "暂无议程项。",
  Add: "添加",
  "brought by": "提出人",
  Discussed: "已讨论",
  "not reached": "未讨论到",
  "What was discussed / decided on this point…": "此议程项讨论/决定的内容…",
  "Mark discussed": "标记为已讨论",
  "Update outcome": "更新结论",
  due: "截止",
  "Start meeting": "开始会议",
  "Close & file minutes": "结束并存档纪要",
  "Meeting in progress — record each point as you go.": "会议进行中 — 逐项记录讨论结果。",
  "set the agenda, then start the meeting": "先定议程，再开始会议",
  "Email minutes to the team": "将纪要发送给团队",
  "Re-send minutes": "重发纪要",
  "minutes emailed": "纪要已发送",

  /* renders */
  Renders: "效果图",
  renders: "张效果图",
  "Project renders": "项目效果图",
  "Add renders": "添加效果图",
  "Main render": "主效果图",
  "Set as main": "设为主图",
  "Shown on the project card": "显示在项目卡片上",
  "No renders yet — add the presentation visuals and pick a main one for the project card.":
    "暂无效果图 — 上传方案效果图并选择一张作为项目主图。",

  /* report view + pdf */
  "View report": "查看报告",
  "Download PDF": "下载 PDF",
  "Preparing PDF…": "正在生成 PDF…",
  "Daily Site Report": "每日现场报告",
  "Filed by": "填报人",
  "Acknowledged by": "确认人",
  "Pending acknowledgement": "待确认",
  "Sign-off": "签署",
  "Site photos": "现场照片",

  /* daily reports — composer */
  Conditions: "天气条件",
  "Prefilled from the site forecast — adjust if the day differed": "已按现场天气预报预填 — 如当天不同请调整",
  "Who was on site": "今日到场人员",
  "Workforce by trade, plus team members present": "按工种统计人数，并勾选到场的团队成员",
  Trade: "工种",
  "Add trade": "添加工种",
  Masons: "瓦工",
  Carpenters: "木工",
  "Steel fixers": "钢筋工",
  Electricians: "电工",
  Plumbers: "水暖工",
  Painters: "油漆工",
  Labourers: "小工",
  Operators: "机械操作员",
  "Someone else — name & company": "其他人员 — 姓名及单位",
  Activities: "施工活动",
  activities: "项活动",
  "One card per activity — tag the trade and location, attach its photos": "每项活动一张卡片 — 标注工种与位置，并附上照片",
  "What was done — e.g. blockwork to first floor, units 7–9": "做了什么 — 例如：7–9号楼一层砌块砌筑",
  "Location — block / level / chainage": "位置 — 楼栋/楼层/桩号",
  "Add activity": "添加活动",
  "Attach photos to this activity": "为该活动附加照片",
  observations: "条观察",
  "Anything worth flagging — safety, quality, progress": "任何值得提出的事项 — 安全、质量、进度",
  "What you observed…": "你观察到了什么…",
  "Attach a photo": "附加照片",
  "Raise an observation": "提出观察事项",
  "Add another observation": "再添加一条",
  "Each one lands in the Observations log with its ref, linked to this record.": "每条都会进入观察记录并生成编号，与本报告关联。",
  "Site extras": "其他事项",
  "Equipment, visitors, delays and general photos": "机械设备、来访人员、延误及其他照片",
  "General photos": "现场照片",
  photos: "张照片",
  "On site": "到场",
  "Observations raised": "提出的观察事项",

  /* daily reports */
  "Daily site reports": "每日现场报告",
  "File today's report": "提交今日报告",
  "File a report": "提交报告",
  "File daily site report": "填写每日现场报告",
  "Workforce — last 14 days": "现场人力 — 近14天",
  Weather: "天气",
  Sunny: "晴",
  Cloudy: "多云",
  Rain: "雨",
  Storm: "雷暴",
  "Temp °C": "气温 °C",
  "Workforce on site — “Masons × 8, Labourers × 12”": "现场人力 — “瓦工 × 8，小工 × 12”",
  Equipment: "机械设备",
  "Work completed — one item per line": "完成的工作 — 每行一项",
  "Progress photos": "进度照片",
  "Delays / weather impact": "延误 / 天气影响",
  Visitors: "来访人员",
  "File report": "提交报告",
  Acknowledge: "确认",
  Acknowledged: "已确认",
  "Awaiting review": "待审核",
  "on site": "人在场",

  /* --- navigation: only the sections added since the first pass --------- */
  "3D Models": "三维模型",
  Submittals: "报审文件",
  Correspondence: "函件往来",
  "Site Instructions": "现场指令",
  "Site & architect's instructions received": "已收到的现场及设计方指令",
  "Every instruction issued to you carries its own number. Acknowledge each one so the studio knows it was received, and flag any that carry a cost so it can be turned into a variation.": "每一条发给您的指令都有独立编号。请逐条签收，让设计方知道已收到；如涉及费用，请标注以便转为签证。",
  "Change & Variations": "变更与签证",
  Inspections: "验收检查",
  "Reports & Delivery": "报告与推送",

  /* --- submittals ------------------------------------------------------- */
  "Open a submittal": "新建报审",
  "Awaiting the studio": "待设计方审核",
  "submitted and under review": "已提交，审核中",
  "With the contractor": "待承包商处理",
  "to submit or resubmit": "待提交或重新提交",
  "past the response date": "已超过答复期限",
  "Needs me": "待我处理",
  "Search ref, title or spec section…": "搜索编号、名称或规范章节…",
  "No submittals yet — open one for each shop drawing, sample or method statement the contract calls for.":
    "暂无报审文件。合同要求的每份深化图、样品或施工方案都应在此建立一条记录。",
  "Nothing matches this filter.": "没有符合此筛选的记录。",

  /* --- change & variations ---------------------------------------------- */
  "Change & variations": "变更与签证",
  "Raise a change event": "登记变更事件",
  "Original contract sum": "原合同金额",
  "as awarded": "中标时",
  "not recorded": "未录入",
  "Approved variations": "已批准变更",
  "Revised contract sum": "调整后合同金额",
  "original plus approved": "原金额加已批准变更",
  "In the pipeline": "待决金额",
  "In progress": "处理中",
  "No changes yet. Raise a change event the moment something moves — an RFI answer with cost, an instruction, a site condition.":
    "暂无变更。一旦出现涉及费用的情况——技术询问答复、现场指令或现场状况——请立即登记变更事件。",
  "Nothing in this view.": "此视图下暂无记录。",

  /* --- correspondence ---------------------------------------------------- */
  Issue: "签发",
  "Not acknowledged": "未签收",
  "issued, no receipt yet": "已签发，尚未签收",
  "Awaiting a response": "待回复",
  "a reply is required": "需要正式回复",
  "Carrying cost": "涉及费用",
  "no change event raised yet": "尚未登记变更事件",
  "Nothing issued yet. Instructions, notices and claims recorded here carry their own number and a tracked response.":
    "暂无记录。此处签发的指令、通知与索赔均有独立编号，并跟踪回复情况。",
  "Nothing of this type.": "暂无此类记录。",

  /* --- inspections -------------------------------------------------------- */
  "Start an inspection": "开始检查",
  "New checklist": "新建检查表",
  "started, not signed off": "已开始，尚未签署",
  Failed: "不合格",
  "items raised to the punch list": "已转入缺陷清单",
  "First-time pass rate": "一次合格率",
  "of completed inspections": "占已完成检查",
  Checklists: "检查表",
  "reusable templates": "可重复使用的模板",
  "Build a checklist first — a pre-pour check, a safety walk, a handover list — then run it on site.":
    "请先建立检查表——浇筑前检查、安全巡查、移交清单——然后在现场执行。",
  "No inspections yet. Start one from a checklist.": "暂无检查记录，请从检查表开始。",
  "No checklists yet.": "暂无检查表。",

  /* --- coordination models ------------------------------------------------ */
  "Coordination models": "协调模型",
  "Upload a model": "上传模型",
  "Models issued": "已发布模型",
  "available to site": "现场可查看",
  "Open in the browser": "可在浏览器打开",
  "glTF / GLB": "glTF / GLB 格式",
  "For your own tools": "需用专业软件打开",
  "IFC and other formats": "IFC 及其他格式",
  "Open the model": "打开模型",
  Download: "下载",
  "No models yet. Publish the coordination model as glTF or GLB and the contractor can open it here on any phone — no licence, no download.":
    "暂无模型。将协调模型导出为 glTF 或 GLB 发布后，承包商可直接在手机浏览器中打开，无需授权、无需下载。",
  "No models have been issued yet.": "尚未发布任何模型。",
  "glTF and GLB open directly in the browser. IFC is carried here so everyone works from the same file, but it is read in Revit, Navisworks, Solibri or a free IFC viewer — export a glTF alongside it if site needs to open it on a phone.":
    "glTF 与 GLB 可直接在浏览器中打开。IFC 文件在此保存以确保各方使用同一版本，但需在 Revit、Navisworks、Solibri 或免费 IFC 查看器中打开——若现场需用手机查看，请同时导出一份 glTF。",

  /* --- renders -------------------------------------------------------------- */
  "Adjust framing": "调整取景",

  /* --- dashboard stats ------------------------------------------------------ */
  "Workforce today": "今日出勤",
  reported: "报告于",
  "no report yet": "尚未提交报告",
  "Open RFIs": "待答复技术询问",
  overdue: "项逾期",
  "none overdue": "无逾期",
  "Observations open": "未关闭观察项",
  logged: "项已登记",
  "Today's targets": "今日目标",
  of: "/",
  done: "已完成",
  "Materials pending": "待处理材料",
  "awaiting review / approval": "等待审核或批准",
  "Punch items open": "未关闭缺陷",
  closed: "项已关闭",

  /* ---- daily report composer & records (completed 2026-07-22) ---- */
  "Add a challenge": "添加困难",
  "Add a person": "添加人员",
  "Add an area": "添加区域",
  "Add an area for each part of the site that moved today.": "为当天有进展的每个部位各添加一个区域。",
  "Add an information request": "添加信息请求",
  "Anything you are waiting on. It lands at the top of the report, dated.": "任何您正在等待的事项。它会置于报告顶部并标注日期。",
  "Cast — curing": "已浇筑 — 养护中",
  "Certification / licence & expiry": "证书 / 执照及有效期",
  "Challenges & information needed": "困难与所需信息",
  "Confirm remove": "确认移除",
  "Drop renders here, or click to browse (max 8 per batch)": "将效果图拖到此处，或点击浏览（每批最多 8 张）",
  "Employer / sub-contractor": "业主 / 分包商",
  "Finish": "完成",
  "Hrs": "小时",
  "ID / badge no.": "证件 / 工牌号",
  "Jot the shorthand — it expands each line into a proper entry and cites the drawing sheet it belongs to.": "随手记下要点 —— 它会把每一行扩写为完整条目，并注明所属的图纸编号。",
  "Kept at higher resolution than site photos.": "保存分辨率高于普通现场照片。",
  "Milestone": "里程碑",
  "Name": "姓名",
  "Name each person — this is the record of who was here, not just how many": "逐一记录姓名 —— 这是“谁在现场”的记录，而不仅是人数",
  "Nothing in the way today — leave it empty and the report says so.": "今天没有任何阻碍 —— 留空即可，报告会如实说明。",
  "Optional, but it is the record of exactly who was on site — worth keeping on days that matter.": "可选，但这是准确记录现场人员的凭证 —— 在关键的日子值得保留。",
  "Personnel on site": "在场人员名单",
  "Photos": "照片",
  "Remove sheet…": "移除图纸…",
  "Remove this sheet from the register": "从登记册中移除此图纸",
  "Remove this superseded revision": "移除此已作废版本",
  "Role": "角色",
  "Sheets": "图纸",
  "Slowing work": "拖慢施工",
  "Start": "开始",
  "Summary": "摘要",
  "Targets for tomorrow": "明日目标",
  "That file could not be read as an image. PNG or JPEG works best.": "该文件无法作为图片读取。建议使用 PNG 或 JPEG。",
  "The date after which this starts costing time": "自此日期之后开始造成工期损失",
  "The first render becomes the project's cover — you can change it any time with “Set as main”.": "第一张效果图会成为项目封面 —— 您可随时通过“设为主图”更改。",
  "The parts of the site worked today and what state each is in": "当天施工的部位及各自状态",
  "These go to the top of the report — they are why it gets read today": "这些会置于报告顶部 —— 是报告当天被阅读的原因",
  "Watching": "关注中",
  "What is happening there": "那里的情况",
  "What is in the way": "有什么阻碍",
  "What it holds up": "它影响了什么",
  "What you commit to next — tomorrow's report is read against this": "您承诺的下一步 —— 明日报告将据此对照",
  "What you have done or propose to do about it": "您已采取或拟采取的措施",
  "What you need to keep going": "您继续施工所需的东西",
  "Where": "位置",
  "Withdraw this issue (previous revision becomes current)": "撤回本次签发（上一版本恢复为当前版本）",
  "Withdraw — reinstates previous": "撤回 —— 恢复上一版本",
  "Work areas": "工作区域",
  "Work stopped": "停工",
  "Workforce": "劳动力",
  "Works completed": "已完成工作",
  "Write these up properly": "帮我润色成正式记录",
  "Writing them up…": "正在润色…",
  "Yes — remove": "是 —— 移除",
  "none": "无",
  "open actions": "项未完成待办",
  "sheet": "张图纸",

  /* ---- shared / empty states ---- */
  "Project not found.": "未找到项目。",
  "Nothing here.": "暂无内容。",
  "quality": "质量",

  /* ---- punch list ---- */
  "Punch list": "缺陷清单",
  "Raise item": "提出缺陷项",
  "Ready for review": "待复核",
  "Work done — submit": "已完成 — 提交复核",
  "Close": "关闭",
  "awaiting studio review": "等待工作室复核",
  "Raise punch item": "提出缺陷项",
  "Defect": "缺陷",
  "Honeycombing to column face…": "如：柱面蜂窝麻面…",
  "Unit 3, porch": "如：3栋门廊",
  "Due": "截止日期",
  "Raise": "提交",
  "Concrete": "混凝土",
  "Masonry": "砌筑",
  "Carpentry": "木工",
  "Joinery": "细木工",
  "MEP": "机电",
  "Painting": "油漆",
  "Civil": "土建",
  "Engineer": "工程师",

  /* ---- daily targets ---- */
  "field production": "现场产出",
  "Daily targets": "每日目标",
  "Previous day": "前一天",
  "Next day": "后一天",
  "Today": "今天",
  "carried over": "顺延",
  "No targets for this day.": "当天暂无目标。",
  "No targets for this day yet — add the first below.": "当天暂无目标 —— 在下方添加第一条。",
  "Set a target — e.g. Cast ring beam, unit 10": "设定一个目标 —— 例如：浇筑 10 栋圈梁",
  "End of day": "收工总结",
  "target still open on this day.": "项目标当天仍未完成。",
  "targets still open on this day.": "项目标当天仍未完成。",
  "Roll what didn't finish onto the next morning — it stays flagged as carried over.": "把未完成的顺延到次日早晨 —— 会标记为顺延项。",
  "Everything on this day is closed out.": "当天任务已全部完成。",
  "Carry": "顺延",
  "to": "至",
  "Week commencing": "本周起始",
  "Issue the weekly site report as a PDF": "生成本周现场报告 PDF",
  "commitments met this week": "项本周承诺已完成",
  "Remove": "移除",
  "No weekly commitments set. These are the two or three things the week is judged on.": "尚未设定周承诺。这是衡量本周的两三件关键事项。",
  "Complete first-floor blockwork": "完成一层砌体",
  "Day by day": "逐日",

  /* ---- progress reports ---- */
  "reports issued": "份已签发",
  "Progress reports": "进度报告",
  "Draft report": "起草报告",
  "Issued": "签发于",
  "No reports issued yet.": "尚未签发任何报告。",
  "Draft progress report": "起草进度报告",
  "Progress report — July": "如：进度报告 — 7 月",
  "Period": "周期",
  "July 2026": "如：2026 年 7 月",
  "Where the works stand, what moved, what's at risk…": "工程现状、进展、存在的风险…",
  "Highlights — one per line": "亮点 —— 每行一项",
  "Overall progress": "总体进度",
  "programme suggests": "计划建议",
  "Issue report": "签发报告",

  /* ---- gallery ---- */
  "Site gallery": "现场照片库",
  "Add photos": "添加照片",
  "Site photo": "现场照片",
  "No photos tagged": "没有标记为",
  "add the first from site.": "从现场添加第一张。",
  "No photos yet — add the first from site.": "暂无照片 —— 从现场添加第一张。",
  "Add site photos": "添加现场照片",
  "Compressing…": "压缩中…",
  "Drop photos here, or click to browse (max 8 per batch)": "将照片拖到此处，或点击浏览（每批最多 8 张）",
  "Photos are compressed on-device before syncing.": "照片会在本机压缩后再同步。",
  "Caption…": "图注…",
  "Activity tag": "活动标签",
  "Blockwork, Drainage…": "如：砌体、排水…",

  /* ---- work program / gantt ---- */
  "updated": "更新于",
  "by": "由",
  "Work program": "施工进度计划",
  "Import programme": "导入计划",
  "actual (fill = progress)": "实际（填充=进度）",
  "baseline": "基线",
  "behind programme": "落后于计划",
  "The contractor imports programme updates; the first import fixes the baseline so every later revision shows slippage against it. Click an activity to update progress.": "由承包商导入计划更新；首次导入将固定基线，之后每次修订都会显示相对基线的滞后。点击某项活动可更新进度。",
  "No programme yet — add activities or import the contractor's programme.": "暂无计划 —— 添加活动或导入承包商的计划。",
  "Activity": "活动",
  "Edit activity": "编辑活动",
  "Update activity": "更新活动",
  "Roof structure, block B": "如：B 区屋面结构",
  "Phase": "阶段",
  "End": "结束",
  "Save": "保存",
  ".mpp is Microsoft Project's closed binary format — open it in MS Project and Save As → XML, then drop that file here.": ".mpp 是 Microsoft Project 的封闭二进制格式 —— 请在 MS Project 中打开并另存为 → XML，再将该文件拖到此处。",
  "Loaded": "已加载",
  "Couldn't read that file.": "无法读取该文件。",
  "Nothing to import.": "没有可导入的内容。",
  "Drop a PDF programme, a Microsoft Project XML export (.mpp → Save As → XML), or a CSV — one activity per line: Name, Phase, Start, End, Progress. Activities matching an existing name keep their baseline, so slippage stays visible.": "拖入 PDF 进度计划、Microsoft Project XML 导出文件（.mpp → 另存为 → XML）或 CSV —— 每行一项活动：名称、阶段、开始、结束、进度。名称与现有活动匹配的会保留其基线，因此滞后始终可见。",
  "Reading the programme…": "正在读取计划…",
  "Drop .pdf / .xml (MS Project) / .csv here, or click to browse": "将 .pdf / .xml (MS Project) / .csv 拖到此处，或点击浏览",
  "…or paste the rows": "…或粘贴行数据",
  "Import & track": "导入并跟踪",

  /* ---- site visits ---- */
  "visits": "次巡查",
  "open directive": "项未完成指令",
  "open directives": "项未完成指令",
  "Site visits": "现场巡查",
  "Record site visit": "记录现场巡查",
  "Visit reports are issued by the studio after each walk — tick directives off as your team completes them.": "每次巡查后由工作室签发巡查报告 —— 待您的团队完成后逐项勾销指令。",
  "issued by": "签发人",
  "directives closed": "项指令已关闭",
  "Works inspected": "已检查工作",
  "Observations & reportings": "观察与反映",
  "Tracked in the log:": "已在日志中跟踪：",
  "Directives": "指令",
  "Photo record": "照片记录",
  "No site visits recorded yet.": "尚未记录现场巡查。",
  "The visit": "巡查",
  "Purpose, date and the conditions on the walk": "目的、日期及巡查时的状况",
  "Purpose": "目的",
  "Monthly client walk / pour witness / quality check…": "如：月度业主巡视 / 浇筑见证 / 质量检查…",
  "Who attended": "参加人员",
  "Tap to add from the project team, or type anyone else": "点击从项目团队中添加，或输入其他人员",
  "What you walked and how it looked — attach photos per item": "您巡查的部位及其状况 —— 每项附上照片",
  "Blockwork line & plumb at units 7–8 — within tolerance": "如：7–8 栋砌体轴线与垂直度 —— 在允许偏差内",
  "Each becomes a numbered item on the report AND a tracked OBS in the log": "每条都会成为报告上的编号条目，并在日志中作为可跟踪的观察记录",
  "Actions for the contractor — assignee and due date per item": "给承包商的行动项 —— 每项含负责人与截止日期",
  "Re-roll drainage bedding 0+100–0+140": "如：重新碾压排水垫层 0+100–0+140",
  "Add directive": "添加指令",
  "Shots taken on the walk — plus anything already in the gallery": "巡查时拍摄的照片 —— 以及图库中已有的照片",
  "Upload from the walk": "巡查上传",
  "Attach from the gallery (taken around that date)": "从图库附加（该日期前后拍摄）",
  "attendees": "名参加人员",
  "inspected": "项已检查",
  "directives": "项指令",
  "Issue visit report": "签发巡查报告",

  /* ---- documents ---- */
  "on file": "份已归档",
  "Documents": "文档",
  "File a document": "归档文件",
  "documents lapsed": "份文件已过期",
  "documents expiring soon": "份文件即将到期",
  "On file": "已归档",
  "across every folder": "涵盖所有文件夹",
  "Expiring within 45 days": "45 天内到期",
  "insurance, bonds, permits": "保险、保函、许可",
  "Lapsed": "已过期",
  "past their date": "已过有效期",
  "Shared with the client": "已与业主共享",
  "visible in their portal": "在业主门户可见",
  "Document": "文件",
  "Folder": "文件夹",
  "Issued by": "签发方",
  "Expires": "到期",
  "Filed": "归档于",
  "lapsed": "已过期",
  "Shared with the client — click to make it internal": "已与业主共享 —— 点击改为内部",
  "Internal — click to share with the client": "内部 —— 点击与业主共享",
  "Nothing filed yet — the building permit, the contractor's insurance, the performance bond and the method statements all belong here.": "尚无归档 —— 施工许可、承包商保险、履约保函及施工方案都应放在这里。",
  "Nothing in this folder.": "此文件夹为空。",
  "The upload didn't complete — check the connection and try again.": "上传未完成 —— 请检查网络后重试。",
  "File": "文件",
  "Contractor's all-risks insurance": "如：承包商一切险",
  "Guyana Fire Service / insurer": "如：消防部门 / 保险公司",
  "Expires on": "到期日",
  "(optional)": "（可选）",
  "normally lapses — set the date and the project warns you 45 days out.": "通常会到期 —— 设定日期后，项目会在到期前 45 天提醒您。",
  "Policy number, conditions, anything worth remembering": "保单号、条件，或任何值得记录的信息",
  "Most of this is internal; permits and warranties are usually the exceptions.": "大部分为内部资料；许可与保修通常例外。",
  "Filing…": "归档中…",
  "File it": "归档",
  "Permits & approvals": "许可与批复",
  "Insurance": "保险",
  "Bonds & guarantees": "保函与担保",
  "Contract": "合同",
  "Warranties": "保修",
  "Method statements": "施工方案",
  "Test certificates": "检验证书",
  "Handover & O&M": "移交与运维",

  /* ---- valuations ---- */
  "valuations": "期计量",
  "Valuations": "计量",
  "Schedule of values": "价值明细表",
  "Open a valuation": "开立计量",
  "original plus approved variations": "原合同额加已批准变更",
  "Certified to date": "累计已核证",
  "certificates": "份证书",
  "Complete by value": "按金额完成度",
  "of the revised sum": "占修订后合同额",
  "Certified, unpaid": "已核证未付",
  "awaiting payment": "等待付款",
  "Set the schedule of values first.": "请先设定价值明细表。",
  "It is the breakdown the contract sum is measured against — trade by trade, or the priced BOQ sections. Every valuation then measures progress line by line.": "这是合同额据以计量的分解 —— 按工种，或按已计价的工程量清单章节。之后每期计量都逐行核算进度。",
  "No valuations yet — open the first one when the period closes.": "尚无计量 —— 计量期结束后开立第一期。",
  "Valuation to": "计量截至",
  "Prepared by": "编制人",
  "certified": "已核证",
  "Value": "金额",
  "Complete": "完成度",
  "This valuation": "本期计量",
  "No schedule of values on this valuation.": "本期计量无价值明细表。",
  "Measured work to date": "累计已完成工程",
  "Materials on site": "现场材料",
  "Gross value to date": "累计总额",
  "Less retention": "减保留金",
  "Net value to date": "累计净额",
  "Less previously certified": "减以往已核证",
  "Due this certificate": "本期应付",
  "Retention %": "保留金 %",
  "Certify": "核证",
  "Record payment": "登记付款",
  "Open the Contract Generator with this valuation's figures to raise the payment certificate": "用本期计量数据打开合同生成器以开具付款证书",
  "Raise the certificate": "开具证书",
  "Remove this valuation": "删除本期计量",
  "Valued to": "计量截至",
  "The valuation opens with the schedule of values, every approved variation and whatever the last certificate paid — you measure the percentages and certify the balance.": "计量以价值明细表、每项已批准变更以及上一份证书已付金额开立 —— 由您核算百分比并核证余额。",
  "Open it": "开立",
  "The breakdown the contract sum is measured against — by trade, by section, or straight from the priced BOQ.": "合同额据以计量的分解 —— 按工种、按章节，或直接取自已计价的工程量清单。",
  "Substructure — excavation & foundations": "如：基础工程 —— 开挖与基础",
  "Add a line": "添加一行",
  "Schedule total": "明细表合计",
  "Matches the contract sum": "与合同额一致",
  "over": "超出",
  "under": "不足",
  "Save schedule": "保存明细表",
  "Being prepared": "编制中",
  "Submitted for certification": "已提交待核证",
  "Certified": "已核证",
  "Paid": "已支付",

  /* ---- reports & delivery ---- */
  "scheduled": "项定时",
  "saved views": "个已保存视图",
  "Reports & delivery": "报告与推送",
  "Schedule a report": "设定定时报告",
  "Email delivery isn't connected yet": "邮件推送尚未接通",
  "schedules are saved and will start sending the moment a RESEND_API_KEY is bound to the worker.": "定时任务已保存，一旦 worker 绑定 RESEND_API_KEY 即开始发送。",
  "Open items": "未完成事项",
  "across every register": "涵盖所有登记册",
  "Scheduled": "已定时",
  "active": "个启用",
  "Saved views": "已保存视图",
  "kept filters": "保留的筛选",
  "Paused": "已暂停",
  "Last send failed": "上次发送失败",
  "To": "发送至",
  "— nobody yet —": "— 暂无收件人 —",
  "last sent": "上次发送",
  "never sent": "从未发送",
  "Send now": "立即发送",
  "Pause": "暂停",
  "Resume": "恢复",
  "Edit": "编辑",
  "Remove this schedule": "删除此定时任务",
  "Nothing is scheduled. A Monday client digest and a daily open-items list to the contractor cover most of what a project needs to keep moving without being chased.": "尚未设定任何定时。每周一给业主的进度简报，加上每日发给承包商的未完成事项清单，基本能覆盖项目无需催促即可推进所需的内容。",
  "What an open-items report says today": "今日未完成事项报告内容",
  "Every day at": "每天",
  "Every": "每",
  "of each month at": "日，每月，",
  "Monday": "星期一",
  "Tuesday": "星期二",
  "Wednesday": "星期三",
  "Thursday": "星期四",
  "Friday": "星期五",
  "Saturday": "星期六",
  "Sunday": "星期日",
  "Monday client digest": "如：周一业主简报",
  "What goes out": "发送内容",
  "Which register": "哪个登记册",
  "— pick one —": "— 请选择 —",
  "How often": "频率",
  "Every day": "每天",
  "Every week": "每周",
  "Every month": "每月",
  "Day": "星期",
  "Hour (UTC)": "时刻（UTC）",
  "Send to — comma separated": "发送至 —— 逗号分隔",
  "The worker checks every hour and posts whatever is due, once per slot. Times are UTC — Guyana is UTC−4, so 12:00 UTC lands at 08:00 locally.": "worker 每小时检查一次并发送到期内容，每个时段一次。时间为 UTC —— 圭亚那为 UTC−4，因此 UTC 12:00 对应当地 08:00。",
  "Save changes": "保存更改",
  "Schedule it": "设定",
  "Open items — everything still owed": "未完成事项 —— 所有尚欠事项",
  "Client digest — progress in plain language": "业主简报 —— 通俗语言说明进度",
  "One register": "单个登记册",
  "Programme": "进度计划",

  /* ---- RFIs (single-language display + acknowledge) ---- */
  "studio": "工作室",
  "Client": "业主",
  "asked": "提问于",
  "on sheet": "于图纸",
  "Ball in court": "责任方",
  "Awaiting the studio's answer": "等待工作室答复",
  "No RFIs": "暂无技术询问",
  "Answer": "答复",
  "Draft the answer…": "拟写答复…",
  "Acknowledge answer": "确认答复",
  "Still unclear": "仍不清楚",
  "Send back to the studio": "退回工作室",
  "Show the other language": "显示另一种语言",

  /* ---- daily report: today vs the plan ---- */
  "Today vs the plan": "今日 vs 计划",
  "outstanding": "未完成",
  "Carry the unfinished into tomorrow": "将未完成项顺延至明天",
  "Everything promised for today is covered by the activities above.": "今日承诺的事项均已被上方的施工活动覆盖。",
  "Share": "分享",
};

/* The later volumes — see i18nZh2.ts. Split only so no one file becomes
   unreadable; the lookup is one flat dictionary. */
let DICT: Record<string, string> = ZH;
if (lang === "zh") void loadZh();
